// A small, dependency-free tooltip. Native `title=` lags ~700ms and is easy to
// clip inside the sidebar's overflow:auto container; this shows a styled label on
// hover AND keyboard focus after a short delay, and renders the bubble through a
// portal to <body> with position:fixed so the narrow scroll container can never
// clip it. It is supplementary: keep the trigger's own aria-label for screen
// readers (the bubble carries role="tooltip" and is wired via aria-describedby).

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

export interface TooltipProps {
  /** The visible tooltip text. */
  label: string;
  /** Preferred side relative to the trigger. Defaults to 'top'. */
  side?: 'top' | 'bottom';
  /** Delay before showing, in ms. Short by design (native title is ~700ms). */
  delay?: number;
  /** Optional extra class on the inline trigger wrapper. */
  className?: string;
  children: ReactNode;
}

interface BubblePos {
  left: number;
  top: number;
  side: 'top' | 'bottom';
}

/** Keep the bubble's anchor a few px inside the viewport so it never bleeds off. */
function clampToViewport(x: number): number {
  if (typeof window === 'undefined') return x;
  const margin = 8;
  return Math.min(Math.max(x, margin), window.innerWidth - margin);
}

export function Tooltip({
  label,
  side = 'top',
  delay = 120,
  className,
  children,
}: TooltipProps): ReactElement {
  const [pos, setPos] = useState<BubblePos | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tipId = useId();

  const show = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setPos({
        left: clampToViewport(r.left + r.width / 2),
        top: side === 'top' ? r.top : r.bottom,
        side,
      });
    }, delay);
  }, [delay, side]);

  const hide = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    setPos(null);
  }, []);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  return (
    <span
      ref={triggerRef}
      className={className ? `mp-tip ${className}` : 'mp-tip'}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      aria-describedby={pos ? tipId : undefined}
    >
      {children}
      {pos &&
        typeof document !== 'undefined' &&
        createPortal(
          <span
            id={tipId}
            role="tooltip"
            className="mp-tip__bubble"
            data-side={pos.side}
            style={{ position: 'fixed', left: pos.left, top: pos.top }}
          >
            {label}
          </span>,
          document.body,
        )}
    </span>
  );
}
